/**
 * KnowledgeBreadcrumb — hairline bar under KnowledgeTopBar showing the
 * "Knowledge Center › Collection › Article" trail. The last item is always
 * the current page and renders as plain text (not a link).
 */

import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { media } from '../../../../styles/media';

export interface KnowledgeBreadcrumbItem {
  label: string;
  to?: string;
}

export interface KnowledgeBreadcrumbProps {
  items: KnowledgeBreadcrumbItem[];
}

const Bar = styled.div`
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
`;

const Wrap = styled.nav`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing[8]};
  height: 44px;
  display: flex;
  align-items: center;

  ${media.tablet} {
    padding: 0 ${({ theme }) => theme.spacing[4]};
    overflow-x: auto;
  }
`;

const List = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.text.muted};
  white-space: nowrap;
`;

const Crumb = styled.li`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const CrumbLink = styled(Link)`
  color: ${({ theme }) => theme.colors.text.muted};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};

  &:hover {
    color: ${({ theme }) => theme.colors.primary.base};
  }
`;

const Sep = styled.span`
  color: ${({ theme }) => theme.colors.borders.medium};
`;

const Current = styled.span`
  color: ${({ theme }) => theme.colors.text.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
`;

export function KnowledgeBreadcrumb({ items }: KnowledgeBreadcrumbProps) {
  return (
    <Bar>
      <Wrap aria-label="Breadcrumb">
        <List>
          {items.map((item, i) => {
            const isLast = i === items.length - 1;
            return (
              <Crumb key={i}>
                {i > 0 && <Sep aria-hidden="true">›</Sep>}
                {item.to && !isLast ? (
                  <CrumbLink to={item.to}>{item.label}</CrumbLink>
                ) : (
                  <Current aria-current={isLast ? 'page' : undefined}>{item.label}</Current>
                )}
              </Crumb>
            );
          })}
        </List>
      </Wrap>
    </Bar>
  );
}

export default KnowledgeBreadcrumb;
